import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { loadTariffe } from '../lib/dataloader'
import { generaPdfPratica } from '../lib/pdfPratica'
import { formatEuro } from '../lib/calcoli'

// Pagina di conferma post-invio.
// Si raggiunge da StepInvio dopo che l'Edge Function 'invia-pratica' è andata a buon fine.
// Riceve i dati della pratica via location.state (preferito) ma è in grado di
// ricaricarli dal DB se l'utente arriva per refresh / link diretto.
export default function PraticaInviata() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const { profile } = useAuth()

  const [dati, setDati] = useState(location.state || null)
  const [loading, setLoading] = useState(!location.state)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const downloadTriggered = useRef(false)

  // Ricarica dal DB se siamo arrivati senza state (es. refresh pagina / link diretto).
  // Attendo che `profile` sia caricato: serve per il controllo di proprietà sotto.
  useEffect(() => {
    if (dati || !id || !profile) return
    let cancelled = false
    ;(async () => {
      try {
        const [{ data: row, error: err }, tariffe] = await Promise.all([
          supabase.from('pratiche').select('*').eq('id', id).maybeSingle(),
          loadTariffe(),
        ])
        if (cancelled) return
        if (err) { setError(err.message); setLoading(false); return }
        if (!row) { setError('Pratica non trovata.'); setLoading(false); return }
        // SICUREZZA: questa è la pagina di conferma DEL DEALER proprietario,
        // con auto-download del PDF di tutti i dati del cliente. Non deve essere
        // accessibile via link condiviso ad altri utenti — nemmeno agli admin,
        // a cui la RLS concede la lettura delle pratiche in processamento.
        // Solo chi ha creato la pratica può vedere questa schermata.
        if (row.dealer_id !== profile.id) {
          setError('Non hai accesso a questa pratica.')
          setLoading(false)
          return
        }
        const compagniaScelta = row.scelta_compagnia
          ? (tariffe.compagnie || []).find(c => c.codice === row.scelta_compagnia)
          : null
        setDati({
          data: {
            tipo_operazione: row.tipo_operazione,
            cliente_nome: row.cliente_nome,
            cliente_cognome: row.cliente_cognome,
            dati_comuni: row.dati_comuni || {},
            compagnie_selezionate: row.compagnie_selezionate || [],
            preventivi: row.preventivi || {},
            standalone_selezionati: row.standalone_selezionati || [],
            scelta_compagnia: row.scelta_compagnia,
            totale_finale: row.totale_finale,
            created_at: row.created_at,
          },
          numeroPratica: row.numero_pratica,
          compagniaScelta,
          tariffe,
        })
        setLoading(false)
      } catch (e) {
        if (!cancelled) { setError(e.message); setLoading(false) }
      }
    })()
    return () => { cancelled = true }
  }, [id, dati, profile])

  async function scaricaPdf() {
    if (!dati) return
    setDownloading(true)
    try {
      await generaPdfPratica({
        data: dati.data,
        profile,
        compagniaScelta: dati.compagniaScelta,
        tariffe: dati.tariffe,
        numeroPratica: dati.numeroPratica,
      })
    } catch (e) {
      setError(`Errore generazione PDF: ${e.message}`)
    } finally {
      setDownloading(false)
    }
  }

  // Trigger automatico del download del PDF non appena abbiamo i dati.
  // Niente setTimeout: in React StrictMode (dev) il timer veniva cancellato
  // dal cleanup e il download non partiva.
  useEffect(() => {
    if (!dati || downloadTriggered.current) return
    downloadTriggered.current = true
    scaricaPdf()
  }, [dati])

  if (error) {
    return (
      <div>
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/pratiche')}>← Vai alle mie pratiche</button>
      </div>
    )
  }

  if (loading || !dati) {
    return <div className="loading">Caricamento dati pratica…</div>
  }

  const d = dati.data
  const dc = d.dati_comuni || {}
  const isSoloSA = d.tipo_operazione === 'solo_standalone'
  const isAutocarri = d.tipo_operazione === 'autocarri_intermediari'

  return (
    <div>
      <div className="page-header">
        <h1>✓ Pratica inviata</h1>
        <p>L'amministrazione di Diemmeci Broker ha ricevuto la pratica e provvederà a processarla. Riceverai una mail con l'esito.</p>
      </div>

      <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
        <strong>Pratica trasmessa con successo</strong>
        {dati.numeroPratica && <> · Numero pratica <strong>{dati.numeroPratica}</strong></>}
        <div className="text-small" style={{ marginTop: 6 }}>
          Il PDF riassuntivo viene scaricato automaticamente. Conservalo come copia per i tuoi archivi.
        </div>
      </div>

      <div className="btn-row" style={{ marginBottom: '1.5rem', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <button className="btn btn-primary" onClick={scaricaPdf} disabled={downloading}>
          {downloading ? 'Generazione PDF…' : '⤓ Scarica il PDF riassuntivo'}
        </button>
        <button className="btn" onClick={() => navigate('/pratiche')}>
          Vai alle mie pratiche →
        </button>
      </div>

      <div className="card">
        <h2 className="card-title">Riepilogo</h2>
        <div className="stat-grid">
          <div className="stat-card">
            <div className="sl">Contraente</div>
            <div className="sv">{dc.is_societa ? d.cliente_nome : `${d.cliente_cognome || ''} ${d.cliente_nome || ''}`.trim()}</div>
          </div>
          <div className="stat-card">
            <div className="sl">Targa</div>
            <div className="sv">{(dc.targa || '—').toUpperCase()}</div>
          </div>
          {!isSoloSA ? (
            <>
              <div className="stat-card">
                <div className="sl">Compagnia</div>
                <div className="sv">{isAutocarri ? 'Wakam' : (dati.compagniaScelta?.nome || d.scelta_compagnia || '—')}</div>
              </div>
              <div className="stat-card">
                <div className="sl">Durata</div>
                <div className="sv">{dc.durata_mesi ? `${dc.durata_mesi} mesi` : '—'}</div>
              </div>
            </>
          ) : (
            <div className="stat-card">
              <div className="sl">Tipo</div>
              <div className="sv">Solo Stand-alone</div>
            </div>
          )}
          <div className="stat-card">
            <div className="sl">Totale</div>
            <div className="sv">{formatEuro(d.totale_finale)}</div>
          </div>
        </div>
      </div>

    </div>
  )
}
